import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppModal,
  AppNoInfo,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../../components/helpers/Translator';
import {
  getInherentImpact,
  getPriority,
  getRiskControlLevel,
  getSeverity,
} from '../../tools/totalEnergiesRiskRiskTools';

export default function AssessmentTab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const locale = useSelector((state) => state.global?.urlData?.lang);
  const level1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1
  );
  const riskLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID]
  );
  const assessment = useSelector(
    (s) => s.currentPage?.assessmentTab?.[elementID]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(<Translator file="user" name="general.no" />),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(<Translator file="user" name="general.yes" />),
      value: 1,
    },
  ]);
  const [noDisabledYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(<Translator file="user" name="general.no" />),
      value: 0,
      disabled: true,
    },
    {
      label: ReactDOMServer.renderToString(<Translator file="user" name="general.yes" />),
      value: 1,
    },
  ]);
  const [residualImpactOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getInherentImpact(i),
    }))
  );
  const [riskControlLevelOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getRiskControlLevel(i),
    }))
  );
  const validationSchema = Yup.object().shape({
    residualImpact: Yup.number()
      .nullable()
      .required('Requerido'),
    riskControlLevel: Yup.number()
      .nullable()
      .required('Requerido'),
    relevantRisk: Yup.number()
      .nullable()
      .required('Requerido'),
    hasActionPlans: Yup.number()
      .nullable()
      .required('Requerido'),
  });

  // > LOAD ASSESSMENT TAB
  const loadAssessment = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_risk_assessment_tab"),
          { moduleBridgeHex, riskID: elementID }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("assessmentTab", {
                [elementID]: data,
              })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID]
  );

  useEffect(() => {
    if (isLoading) loadAssessment();
  }, [isLoading]);
  // < LOAD ASSESSMENT TAB

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_risk_assessment_edit"),
          {
            moduleBridgeHex,
            riskHex: riskLevel1?.hex,
            ...values,
          }
        )
        .then(({ data }) => {
          dispatch(
            currentPageAction.change(
              "elementLevels",
              {
                level1: {
                  ...level1,
                  [elementID]: { ...riskLevel1, severity: data?.severity, priority: data?.priority },
                },
              },
              true
            )
          );

          setShowEditModal(false);
          setIsLoading(true);
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, riskLevel1]
  );
  // < SAVE

  const getHasActionPlans = useCallback(
    () =>
      assessment?.hasActionPlans === null
        ? '--'
        : assessment?.hasActionPlans === 1
        ? 'Sí'
        : 'No',
    [assessment]
  );

  const handleResidualImpact = useCallback((opt, setFieldValue) => {
    switch (opt) {
      case 0:
      case 1:
        setFieldValue('relevantRisk', 0);
        break;
      case 2:
      case 3:
      case 4:
      case 5:
        setFieldValue('relevantRisk', 1);
        break;
    }

    setFieldValue('residualImpact', opt);
  }, []);

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <>
      <div className="container-fluid">
        {userBridge?.permissions?.level === 0 && (
          <div className="row">
            <div className="col-lg-12">
              <button
                type="button"
                className="btn btn-xs btn-success float-right"
                onClick={() => setShowEditModal(true)}
              >
                Editar
              </button>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-lg-6 mb-2">
            <Title>Impacto residual</Title>
            {assessment?.residualImpact !== null
              ? getInherentImpact(assessment?.residualImpact)
              : '--'}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Nivel de control del riesgo</Title>
            {assessment?.riskControlLevel !== null
              ? getRiskControlLevel(assessment?.riskControlLevel)
              : '--'}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Severidad</Title>
            {assessment?.severity !== null
              ? getSeverity(assessment?.severity)
              : '--'}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Prioridad</Title>
            {assessment?.priority !== null
              ? getPriority(assessment?.priority)
              : '--'}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Requiere Plan de acción</Title>
            {getHasActionPlans()}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Fecha de creación</Title>
            {assessment?.dateStamp}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Fecha última modificación</Title>
            {assessment?.lastUpdateDateStamp || '--'}
          </div>
          <div className="col-lg-6 mb-2">
            <Title>Fecha prevista revisión</Title>
            {assessment?.revisionDateStamp || '--'}
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12 mb-2">
            <Title>Observaciones</Title>
            {assessment?.observations || '--'}
          </div>
        </div>
      </div>
      <AppModal
        show={showEditModal}
        size="lg"
        title="Editar riesgo"
        onClose={() => setShowEditModal(false)}
      >
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          initialValues={{
            residualImpact: assessment?.residualImpact,
            riskControlLevel: assessment?.riskControlLevel,
            relevantRisk: assessment?.relevantRisk,
            hasActionPlans: assessment?.hasActionPlans || 0,
            revisionDateStamp: assessment?.revisionDateStamp,
            observations: assessment?.observations,
          }}
          onSubmit={handleSave}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-6">
                    <AppSingleSelect
                      options={residualImpactOptions}
                      title="Impacto residual"
                      value={values?.residualImpact}
                      error={errors?.residualImpact}
                      onChange={(opt) =>
                        handleResidualImpact(opt, setFieldValue)
                      }
                      required
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppSingleSelect
                      options={riskControlLevelOptions}
                      title="Nivel de control del riesgo"
                      value={values?.riskControlLevel}
                      error={errors?.riskControlLevel}
                      onChange={(opt) => {
                        setFieldValue('riskControlLevel', opt);
                      }}
                      required
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppSingleSelect
                      options={noYesOptions}
                      title="Riesgo Relevante"
                      value={values?.relevantRisk}
                      error={errors?.relevantRisk}
                      onChange={(opt) => {
                        setFieldValue('relevantRisk', opt);
                      }}
                      required
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppSingleSelect
                      options={noDisabledYesOptions}
                      title="Requiere plan de acción"
                      value={values?.hasActionPlans}
                      error={errors?.hasActionPlans}
                      onChange={(opt) => {
                        setFieldValue('hasActionPlans', opt);
                      }}
                      required
                    />
                  </div>
                  <div className="col-lg-12">
                    <AppDatePicker
                      locale={locale}
                      title="Fecha prevista revisión"
                      value={values?.revisionDateStamp}
                      onChange={(opt) =>
                        setFieldValue('revisionDateStamp', opt)
                      }
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                      minDate={Moment()
                        .add(1, 'days')
                        .toDate()}
                    />
                  </div>
                  <div className="col-lg-12">
                    <AppTextArea
                      title="Observaciones"
                      onChange={(value) => setFieldValue('observations', value)}
                      value={values.observations}
                      error={errors.observations}
                      resize
                      isClearable
                    />
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-sm btn-success float-right"
                    disabled={isSubmitting}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger float-right mr-2"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
}

const Title = styled.h3`
  font-size: 13px;
  font-weight: bold;
  margin: 0;
`;
